/**
 * Web auth — trades the Shopify session for the middleware's own JWT.
 *
 * The whole security property sits in one detail: the login POST goes to a RELATIVE path,
 * so it travels through Shopify's app proxy and Shopify appends a signed
 * `logged_in_customer_id` the browser cannot forge. The middleware verifies that
 * signature and mints an RS256 pair from it. Nothing here sends a customer id, and
 * nothing should — needing to would mean the proxy hop, and the signature with it, has
 * been lost.
 *
 * The trap that follows from that: pointing `backend_host` at an absolute host (the
 * documented one-field rollback) bypasses the proxy, so Shopify never signs the request
 * and login answers 401 PROXY_UNAUTHENTICATED every time. It reads exactly like a broken
 * backend and is not one.
 *
 * Consumers do not touch tokens. They swap `fetch(` for `AuthToken.fetch(` at their one
 * transport chokepoint and keep their own response parsing — this returns the raw
 * Response untouched.
 */

const AUTH_STORAGE_KEY = "themex-auth-pair";
const AUTH_BLOCKED_KEY = "themex-auth-blocked";
const AUTH_TIMEOUT_MS = 10000;
// Renew this far ahead of expiry so a request cannot land on a token that died between
// the freshness check and the server reading it.
const AUTH_EXPIRY_SKEW_MS = 60000;
// 503 NOT_CONFIGURED means the backend holds no proxy secret, so every call would fail
// the same way — the breaker stops us adding a round trip to each one. The lapse matters
// more than the breaker itself: wizzy-api-down shipped without one and left tabs pinned
// to a fallback long after the API recovered.
const AUTH_BLOCKED_MS = 300000;
// 502 IDENTITY_CHECK_FAILED is documented as retryable: the backend reached Shopify and
// Shopify did not answer. Every other failure is terminal for this page load.
const AUTH_IDENTITY_RETRIES = 2;

const AuthToken = {
  _config: null,
  _pair: null,
  _inflight: null,
  // Set when the shopper turns out to have no Shopify session. Deliberately in memory
  // rather than sessionStorage: a KwikPass login navigates, and a fresh document is
  // exactly the signal that this should be reconsidered.
  _unauthenticated: false,

  config() {
    if (this._config) return this._config;
    const element = document.querySelector("[data-auth-config]");
    if (!element) return null;
    const { endpoint, customerId } = element.dataset;
    if (!endpoint) return null;
    // Trailing-slash handling is asymmetric across the theme's request helpers, so strip
    // it here rather than emitting `//auth/token/web`.
    this._config = { endpoint: endpoint.replace(/\/$/, ""), customerId: customerId || null };
    return this._config;
  },

  // expiresAt's wire format is not pinned down by the API docs, so accept all three
  // shapes it could arrive as instead of betting on one. Anything unparseable reads as
  // already expired, which costs a refresh rather than sending a dead token.
  _expiryMs(value) {
    if (value === null || value === undefined || value === "") return 0;
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  },

  _wait(ms) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  },

  _blocked() {
    try {
      const until = Number(sessionStorage.getItem(AUTH_BLOCKED_KEY) ?? 0);
      if (until > Date.now()) return true;
      if (until) sessionStorage.removeItem(AUTH_BLOCKED_KEY);
    } catch (error) {
      return false;
    }
    return false;
  },

  _block() {
    try {
      sessionStorage.setItem(AUTH_BLOCKED_KEY, String(Date.now() + AUTH_BLOCKED_MS));
    } catch (error) {
      // An unstored breaker just means the next call retries — no worse than none.
    }
  },

  _readPair() {
    if (this._pair) return this._pair;

    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(AUTH_STORAGE_KEY) ?? "null");
    } catch (error) {
      stored = null;
    }
    if (!stored?.token) return null;

    // A pair minted for a different customer is worse than no pair at all — it would
    // read another shopper's data into this page. Same reasoning as the
    // themex-wishlist-customer comparison in wishlist-store.js.
    if (stored.customerId !== this.config()?.customerId) {
      this.clear();
      return null;
    }

    this._pair = stored;
    return this._pair;
  },

  _writePair(data) {
    const existing = this._pair;
    const pair = {
      token: data.token,
      expiresAt: data.expiresAt ?? null,
      // /auth/refresh answers with only a new access token, so a refresh must not blank
      // the refresh half of the pair.
      refreshToken: data.refreshToken ?? existing?.refreshToken ?? null,
      refreshExpiresAt: data.refreshExpiresAt ?? existing?.refreshExpiresAt ?? null,
      customerId: this.config()?.customerId ?? null,
    };

    this._pair = pair;
    try {
      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(pair));
    } catch (error) {
      // Private mode or quota — the in-memory copy still serves this page.
    }
    return pair;
  },

  clear() {
    this._pair = null;
    try {
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
    } catch (error) {
      // Nothing left to do; the in-memory copy is already gone.
    }
  },

  async _post(path, body) {
    const config = this.config();
    if (!config) return { ok: false, status: 0 };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AUTH_TIMEOUT_MS);

    try {
      const response = await window.fetch(`${config.endpoint}${path}`, {
        method: "POST",
        // Same-origin by construction, since endpoint is the /apps/... proxy path. This
        // only makes the Shopify session cookie explicit — it is what lets the proxy hop
        // resolve logged_in_customer_id.
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(body === undefined ? null : { "Content-Type": "application/json" }),
        },
        ...(body === undefined ? null : { body: JSON.stringify(body) }),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => null);
      return {
        ok: response.ok,
        status: response.status,
        // The auth routes answer with the pair at the top level; the rest of the
        // middleware uses a {success,error,data} envelope. Tolerate both.
        data: payload?.data ?? payload ?? null,
      };
    } catch (error) {
      return { ok: false, status: 0 };
    } finally {
      clearTimeout(timer);
    }
  },

  async login() {
    for (let attempt = 0; attempt <= AUTH_IDENTITY_RETRIES; attempt += 1) {
      const result = await this._post("/auth/token/web");

      if (result.ok && result.data?.token) return this._writePair(result.data);

      // PROXY_UNAUTHENTICATED. Not an error and not retryable: the shopper is a guest
      // until they log in, and logging in navigates.
      if (result.status === 401) {
        this._unauthenticated = true;
        return null;
      }
      if (result.status === 503) {
        this._block();
        return null;
      }
      if (result.status !== 502) return null;
      if (attempt < AUTH_IDENTITY_RETRIES) await this._wait(250 * (attempt + 1));
    }
    return null;
  },

  async refresh() {
    const pair = this._readPair();
    if (!pair?.refreshToken) return null;
    if (this._expiryMs(pair.refreshExpiresAt) <= Date.now()) {
      this.clear();
      return null;
    }

    const result = await this._post("/auth/refresh", { refreshToken: pair.refreshToken });
    if (result.ok && result.data?.token) return this._writePair(result.data);

    // REFRESH_TOKEN_EXPIRED / TOKEN_INVALID — the pair is spent, so drop it and let the
    // caller fall back to a fresh login.
    if (result.status === 401) this.clear();
    return null;
  },

  // Single-flight: every consumer calls this, and a logged-in page load can fire several
  // at once. Without the shared promise each one would mint its own pair.
  getToken() {
    if (this._inflight) return this._inflight;
    this._inflight = this._resolveToken().finally(() => {
      this._inflight = null;
    });
    return this._inflight;
  },

  async _resolveToken() {
    const config = this.config();
    if (!config?.customerId || this._unauthenticated || this._blocked()) return null;

    const pair = this._readPair();
    if (pair?.token && this._expiryMs(pair.expiresAt) - AUTH_EXPIRY_SKEW_MS > Date.now()) {
      return pair.token;
    }

    const renewed = (pair?.refreshToken ? await this.refresh() : null) ?? (await this.login());
    return renewed?.token ?? null;
  },

  // Built through Headers rather than object spread: every caller happens to pass a plain
  // object today, but spreading a Headers instance yields {} and would silently drop the
  // caller's headers — X-Brand-Slug included.
  // A null token is normal, not an error: guests and failed logins still need their
  // request sent, just without an Authorization header.
  _withHeaders(options, token) {
    const headers = new Headers(options.headers ?? undefined);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return { ...options, headers };
  },

  /**
   * Drop-in replacement for fetch() at a consumer's transport chokepoint. Returns the
   * raw Response so the caller's own envelope and error handling stay untouched.
   *
   * With no token it delegates straight to fetch, which is what keeps this additive:
   * a guest, a failed login or a backend that has not shipped yet all leave every
   * feature behaving exactly as it did before.
   */
  async fetch(url, options = {}) {
    const token = await this.getToken();

    const response = await window.fetch(url, this._withHeaders(options, token));
    // Nothing to renew without a token, so a 401 there belongs to the caller.
    if (response.status !== 401 || !token) return response;

    // Retry once, on status alone. Reading the body to confirm TOKEN_EXPIRED would
    // consume the stream the caller still has to parse, and a spent token is the only
    // 401 a correctly-scoped route should produce here.
    // A non-string body cannot be replayed, so those callers get the 401 as-is.
    if (options.body !== undefined && typeof options.body !== "string") return response;

    const renewed = (await this.refresh()) ?? (await this.login());
    if (!renewed?.token) return response;

    return window.fetch(url, this._withHeaders(options, renewed.token));
  },
};

// A stored pair outlives the Shopify session it was minted from: the JWT stays valid for
// its full 30 minutes after a sign-out. So sweep on every document rather than trusting any
// one logout path — this covers the theme's Sign Out link, KwikPass's own logout, checkout,
// a server-side session expiry and a logout in another tab alike. _readPair does the work:
// it compares the pair's customer against the one Liquid rendered and clears on mismatch,
// and a signed-out document renders an empty customer id.
const sweepStaleAuthPair = () => {
  AuthToken._pair = null;
  AuthToken._readPair();
};

sweepStaleAuthPair();

// bfcache restores the JS heap wholesale, so a token cached before the shopper navigated
// away — and possibly signed out — would come back with it. Drop the in-memory copy and
// re-sweep rather than trusting either layer.
window.addEventListener("pageshow", (event) => {
  if (!event.persisted) return;
  AuthToken._unauthenticated = false;
  sweepStaleAuthPair();
});

// Clear on an explicit sign-out immediately, instead of leaving a live token in storage
// for however long the logout navigation takes. Delegated so it covers every sign-out
// link in the theme, present and future.
document.addEventListener("click", (event) => {
  if (event.target?.closest?.("[data-auth-clear]")) AuthToken.clear();
});
